import { apiFetch } from './api';
import type { Contract, Clause } from '@/types';

/**
 * Management accounts are CSV-only (no OCR, no PDF, no companion clauses). The analysis viewer still
 * renders them through the shared contract viewer, so {@link getManagementAccount} returns a
 * contract-shaped, READ-ONLY object whose line items are presented as simple clause-shaped rows
 * (heading = party, text = figure). There is no PDF, and comments/reviews are not supported yet.
 */

/** The detail payload from `GET /api/managementAccounts/{uuid}`. */
interface ManagementAccountEntryDetail {
  partyName: string | null
  value: number | null
  currency: string | null
  valueCanonical: number | null
  partyUuid: string | null
}
interface ManagementAccountDetail {
  uuid: string
  projectUuid: string | null
  category: string | null
  periodStart: string | null
  periodEnd: string | null
  label: string | null
  fileName: string | null
  createdAt: string | null
  entries: ManagementAccountEntryDetail[] | null
}

/**
 * Resolves the project that owns a management account. The URL identifies only the account, so any
 * flow that has just the account id derives its project through this lookup first.
 */
export async function getManagementAccountProject(accountUuid: string): Promise<string> {
  const dto = await apiFetch<{ uuid: string; projectUuid: string }>(
    `managementAccounts/${accountUuid}/project`,
  );
  return dto.projectUuid;
}

function titleCase(s: string | null): string {
  if (!s) return ''
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** Formats one line item's figure for display, e.g. "120,000.00 GBP". */
function formatFigure(entry: ManagementAccountEntryDetail): string {
  if (entry.value == null) return '—'
  const amount = entry.value.toLocaleString(undefined, { maximumFractionDigits: 2 })
  return entry.currency ? `${amount} ${entry.currency}` : amount
}

/** Presents the account's line items as read-only clause-shaped rows for the shared viewer. */
function entriesAsClauses(entries: ManagementAccountEntryDetail[]): Clause[] {
  return entries.map((entry, i) => ({
    uuid: entry.partyUuid ?? `ma-entry-${i}`,
    heading: entry.partyName ?? 'Unknown party',
    text: formatFigure(entry),
    clauseTypeTags: [],
    clauseJustifications: [],
    boundingBoxes: [],
    comments: [],
  }))
}

/**
 * Reads a management account and its line items from `GET /api/managementAccounts/{uuid}`, shaped as a
 * read-only Contract so the analysis route only switches the fetch layer, not its rendering. The rows
 * ride along as {@code clauses}; there is no PDF and no comment/review data.
 */
export async function getManagementAccount(accountUuid: string): Promise<Contract> {
  const account = await apiFetch<ManagementAccountDetail>(`managementAccounts/${accountUuid}`);
  const period = account.label ?? account.periodEnd ?? ''
  const name = [titleCase(account.category), period].filter(Boolean).join(' · ')
  return {
    uuid: account.uuid,
    projectUuid: account.projectUuid ?? undefined,
    nameOfAgreement: name || account.fileName || 'Management account',
    category: 'managementAccount',
    createdAt: account.createdAt ?? undefined,
    clauses: entriesAsClauses(account.entries ?? []),
    comments: [],
    reviewedBy: [],
    parties: [],
  } as Contract
}

export const managementAccountService = {
  getManagementAccount,
  getManagementAccountProject,
};
